'use client';

import {
  Portal,
  createListCollection,
  type SelectContentProps,
  type SelectPositionerProps,
  type SelectRootProps,
  type SelectTriggerProps,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { Select } from '@chakra-ui/react';

export interface AttuneSelectOption {
  value: string;
  label: string;
  group?: string;
  disabled?: boolean;
}

export interface AttuneSelectProps
  extends Omit<
    SelectRootProps<AttuneSelectOption>,
    'collection' | 'defaultValue' | 'onValueChange' | 'value'
  > {
  options: readonly AttuneSelectOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  ariaLabel: string;
  portalled?: boolean;
  triggerProps?: SelectTriggerProps;
  positionerProps?: SelectPositionerProps;
  contentProps?: SelectContentProps;
}

/** Theme-aware replacement for native selects whose expanded list cannot be styled reliably. */
export function AttuneSelect({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder,
  ariaLabel,
  portalled = true,
  triggerProps,
  positionerProps,
  contentProps,
  positioning,
  ...rootProps
}: AttuneSelectProps) {
  const emptyOptionLabel = options.find(option => option.value === '')?.label;
  const resolvedPlaceholder =
    placeholder ||
    (value === '' || (value === undefined && defaultValue === '') ? emptyOptionLabel : undefined);
  const collection = useMemo(
    () =>
      createListCollection({
        items: [...options],
        isItemDisabled: item => Boolean(item.disabled),
      }),
    [options]
  );

  return (
    <Select.Root
      collection={collection}
      value={value === undefined ? undefined : [value]}
      defaultValue={defaultValue === undefined ? undefined : [defaultValue]}
      onValueChange={details => onValueChange?.(details.value[0] ?? '')}
      positioning={{ sameWidth: true, ...positioning }}
      {...rootProps}
    >
      <Select.HiddenSelect />
      <Select.Control>
        <Select.Trigger
          aria-label={ariaLabel}
          minH="var(--select-trigger-height)"
          {...triggerProps}
        >
          <Select.ValueText placeholder={resolvedPlaceholder} />
        </Select.Trigger>
        <Select.IndicatorGroup>
          <Select.Indicator />
        </Select.IndicatorGroup>
      </Select.Control>
      <Portal disabled={!portalled}>
        <Select.Positioner {...positionerProps}>
          <Select.Content p="1" maxH="280px" overflowY="auto" zIndex="popover" {...contentProps}>
            {Array.from(new Set(collection.items.map(item => item.group))).map(group => {
              const items = collection.items
                .filter(item => item.group === group)
                .map(item => (
                  <Select.Item key={item.value} item={item} minH="36px" h="auto" px="3" py="2">
                    <Select.ItemText>{item.label}</Select.ItemText>
                    <Select.ItemIndicator />
                  </Select.Item>
                ));
              return group ? (
                <Select.ItemGroup key={group}>
                  <Select.ItemGroupLabel px="2.5" py="1.5" fontSize="xs" color="ui.text.tertiary">
                    {group}
                  </Select.ItemGroupLabel>
                  {items}
                </Select.ItemGroup>
              ) : (
                items
              );
            })}
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  );
}
